import {
  $applyNodeReplacement,
  createEditor,
  DecoratorNode,
  EditorConfig,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread
} from 'lexical'
import React, { Suspense } from 'react'
import ImageComponent from './ImageComponent'

interface ImagePayload {
  altText: string
  height?: string
  maxWidth?: number
  captionsEnabled?: boolean
  src: string
  width?: string
  showCaption?: boolean
  caption?: LexicalEditor
  key?: NodeKey
}

type SerializedImageNode = Spread<
  {
    altText: string
    height?: string
    width?: string
    maxWidth?: number
    showCaption?: boolean
    src: string
  },
  SerializedLexicalNode
>

function convertImageElement(domNode: Node): { node: ImageNode } | null {
  if (domNode instanceof HTMLImageElement) {
    const { alt: altText, src, width = '100%', height = 'auto' } = domNode
    const node = $createImageNode({
      altText,
      height: height as string,
      src,
      width: width as string
    })
    return { node }
  }
  return null
}

export class ImageNode extends DecoratorNode<React.ReactNode> {
  __src: string
  __altText: string
  __maxWidth: number
  __width: string
  __height: string
  __showCaption: boolean
  __caption: LexicalEditor
  __captionsEnabled: boolean

  constructor(
    src: string,
    altText: string,
    maxWidth: number,
    width: string = 'inherit',
    height: string = 'inherit',
    showCaption: boolean = false,
    caption: LexicalEditor = createEditor(),
    captionsEnabled: boolean = true,
    key?: NodeKey
  ) {
    super(key)
    this.__src = src
    this.__altText = altText
    this.__maxWidth = maxWidth
    this.__width = width
    this.__height = height
    this.__showCaption = showCaption
    this.__caption = caption
    this.__captionsEnabled = captionsEnabled
  }

  static getType(): string {
    return 'image'
  }

  static clone(node: ImageNode): ImageNode {
    return new ImageNode(
      node.__src,
      node.__altText,
      node.__maxWidth,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__captionsEnabled,
      node.__key
    )
  }

  static importJSON(serializedNode: SerializedImageNode): ImageNode {
    const {
      altText,
      height = 'auto',
      width = '100%',
      maxWidth = 600,
      src,
      showCaption
    } = serializedNode
    return $createImageNode({
      altText,
      height,
      maxWidth,
      showCaption,
      src,
      width
    })
  }

  exportDOM(): { element: HTMLElement } {
    const element = document.createElement('img')
    element.setAttribute('src', this.__src)
    element.setAttribute('alt', this.__altText)
    element.setAttribute('width', this.__width)
    element.setAttribute('height', this.__height)
    return { element }
  }

  static importDOM(): { img: () => { conversion: typeof convertImageElement; priority: 0 } } {
    return {
      img: () => ({
        conversion: convertImageElement,
        priority: 0
      })
    }
  }

  exportJSON(): SerializedImageNode {
    return {
      altText: this.getAltText(),
      height: this.__height === 'inherit' ? '100%' : this.__height,
      maxWidth: this.__maxWidth,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      type: 'image',
      version: 1,
      width: this.__width === 'inherit' ? '100%' : this.__width
    }
  }

  setWidthAndHeight(width: string, height: string): void {
    const writable = this.getWritable()
    writable.__width = width
    writable.__height = height
  }

  setShowCaption(showCaption: boolean): void {
    const writable = this.getWritable()
    writable.__showCaption = showCaption
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement('span')
    const theme = config.theme
    const className = theme.image
    if (className !== undefined) {
      span.className = className
    }
    return span
  }

  updateDOM(): false {
    return false
  }

  getSrc(): string {
    return this.__src
  }

  getAltText(): string {
    return this.__altText
  }

  decorate(): React.ReactNode {
    return (
      <Suspense fallback={null}>
        <ImageComponent src={this.__src} altText={this.__altText} nodeKey={this.getKey()} />
      </Suspense>
    )
  }
}

export function $createImageNode({
  altText,
  height = 'auto',
  maxWidth = 700,
  captionsEnabled,
  src,
  width = '100%',
  showCaption,
  caption,
  key
}: ImagePayload): ImageNode {
  return $applyNodeReplacement(
    new ImageNode(src, altText, maxWidth, width, height, showCaption, caption, captionsEnabled, key)
  )
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode
}
